import { useSearchParams } from 'next/navigation'
import { paths } from '@/constants/paths'
import { ProjectMetaProvider } from '@/contexts/ProjectMetaContext'
import { Seo } from '@/layout/Seo'
import { DefaultLayout } from '@/layouts/DefaultLayout'
import { NextPageWithLayout } from '@/layouts/types'
import { getAmbassadorPageBySlug } from '@/services/AmbassadorService/AmbassadorService'
import { CatalogMovie, fetchTitle } from '@/services/ContentCatalog'
import { getContentfulProjectTheme } from '@/services/ProjectThemeService'
import { ProjectMeta, getProjectAbout } from '@/services/ProjectsService'
import {
  GetTheatricalPreReleasePageDataResult,
  getTheatricalPreReleasePageData,
} from '@/services/TheatricalPreReleaseService'
import { AmbassadorPage, AmbassadorVideo, ProjectTheme } from '@/types/codegen-contentful'
import { getItemOrFirstEntry } from '@/utils/array-utils'
import { getServerSidePropsErrorHandler } from '@/utils/nextUtils/nextErrorHandlers'
import { omitUndefineds } from '@/utils/object'
import { isDefined } from '@/utils/primitives/generics'
import { getBasePath } from '@/utils/sitemap/base'
import { useTranslate } from '@/utils/translate/translate-client'
import { loadTranslations } from '@/utils/translate/translate-server'
import { AmbassadorView } from '@/views/AmbassadorView'
import { WatchTheatricalContextProvider } from '@/views/WatchTheatricalView/WatchTheatricalContext'

interface AmbassadorProjectPageProps {
  locale: string
  preview: boolean
  projectSlug: string
  projectMeta: ProjectMeta
  pageData: GetTheatricalPreReleasePageDataResult
  ambassadorPageData: AmbassadorPage
  catalogTitle: CatalogMovie | null
  ambassadorVideos: AmbassadorVideo[]
  contentfulProjectTheme: ProjectTheme
  openGraphImage: string | null
}

const AmbassadorProjectPage: NextPageWithLayout<AmbassadorProjectPageProps> = ({
  locale,
  projectSlug,
  projectMeta,
  pageData,
  ambassadorPageData,
  catalogTitle,
  ambassadorVideos,
  openGraphImage,
  contentfulProjectTheme,
}) => {
  const searchParams = useSearchParams()
  const currentVideo =
    searchParams.has('video') &&
    ambassadorVideos.find((ambassador) => {
      return ambassador.sys.id === searchParams.get('video')
    })
  const { t } = useTranslate('ambassador')
  const path = `${paths.ambassadors.index}/${projectSlug}${currentVideo ? `?video=${currentVideo.sys.id}` : ''}`
  const url = `${getBasePath(locale)}${path}`
  const defaultImgUrl =
    'https://images.angelstudios.com/image/upload/v1756153598/angel-studios/seo/angel-opengraph-5.png'
  const title =
    currentVideo && currentVideo?.title
      ? t('angelVideoAmbassadorTitle', '{{ title }} | Spread the Message of Courage | Angel', {
          title: currentVideo.title.replace(/["]+/g, ''),
        })
      : t('angelAmbassadorTitle', 'Ambassadors Program | Angel')
  const description =
    currentVideo && currentVideo?.description
      ? currentVideo.description
      : t('angelStudiosAmbassadorDescription', 'Find out more about the Ambassadors Program on Angel.com')
  const images = [
    {
      url: currentVideo && currentVideo?.thumbnailUrl ? currentVideo.thumbnailUrl : openGraphImage ?? defaultImgUrl,
      width: 1200,
      alt: description,
    },
  ]

  return (
    <>
      <ProjectMetaProvider project={projectMeta}>
        <Seo
          canonical={url}
          description={description}
          openGraph={{ title, description, url, images }}
          path={path}
          title={title}
        />
        <WatchTheatricalContextProvider pageData={pageData}>
          <AmbassadorView
            catalogTitle={catalogTitle}
            ambassadorPageData={ambassadorPageData}
            contentfulProjectTheme={contentfulProjectTheme}
          />
        </WatchTheatricalContextProvider>
      </ProjectMetaProvider>
    </>
  )
}

export const getServerSideProps = getServerSidePropsErrorHandler<AmbassadorProjectPageProps>(
  async ({ federationClient, locale, next404, params, preview }) => {
    const slug = getItemOrFirstEntry(params.slug)
    if (!slug) return next404()

    const [projectMeta, pageData, ambassadorPageData, contentfulProjectTheme] = await Promise.all([
      getProjectAbout({ slug }, federationClient, { locale, preview }),
      getTheatricalPreReleasePageData({ slug }, federationClient, { locale, preview }),
      getAmbassadorPageBySlug({ slug, opts: { locale, preview } }),
      getContentfulProjectTheme(slug, preview),
    ])

    if (
      !projectMeta ||
      !pageData?.filmDetails?.sys?.id ||
      !pageData?.hasAmbassadorPage ||
      !ambassadorPageData?.sys?.id
    ) {
      return next404()
    }

    const [catalogTitle, translations] = await Promise.all([
      fetchTitle<CatalogMovie>(locale, pageData?.contentCatalogId),
      loadTranslations(locale, ['common', 'ambassador', 'pif', 'sign-up']),
    ])

    const ambassadorVideos = (ambassadorPageData?.ambassadorVideosCollection?.items ?? []).filter(isDefined)

    return {
      props: {
        locale,
        preview,
        projectMeta,
        projectSlug: slug,
        pageData,
        ambassadorPageData,
        contentfulProjectTheme,
        openGraphImage: ambassadorPageData?.openGraphImage ?? null,
        ambassadorVideos,
        catalogTitle: omitUndefineds(catalogTitle) ?? null,
        ...translations,
      },
    }
  },
)

export default AmbassadorProjectPage

AmbassadorProjectPage.getLayout = (page) => {
  return <DefaultLayout isDarkMode={true}>{page}</DefaultLayout>
}
